/**
 * use-landing-pages — 落地页任务 API（ADM-LP-S004 / S005 / S007）
 *
 * 契约：dev-docs `02-API契约/AE-投放落地页.md` §4/§5/§7。全局模块，不传 app_id。
 */

import type {
  CreateLandingPageRequest,
  LandingPageDto,
  UpdateLandingPageRequest,
} from '@novel-admin/shared';
import { type OffsetPagedResult, apiRequest, apiRequestOffsetPaged } from '~/api/client';

/** 列表可选过滤（AE §4.1）：按书 / 按渠道，AND 关系。空值不进 query string。 */
export type LandingPageListFilters = { novel_id?: string; media_source?: string };

export const landingPagesApi = {
  /**
   * 换一个短期预览 URL。产物在私有暂存桶、无公开直链，而 window.open 开的新窗口不带
   * Authorization —— 故先用鉴权请求换一个绑死该任务、5 分钟过期的 token，
   * 再用它拼出的路径直接开窗。
   */
  previewUrl: (id: string): Promise<{ url: string; expires_at: string }> =>
    apiRequest<{ url: string; expires_at: string }>({
      method: 'POST',
      path: `/api/v1/landing-pages/${id}/preview-token`,
    }),

  /**
   * 取单文件自包含 HTML（ADM-LP-S008），供运营上传投放平台。
   * 走 JSON 壳 + 前端造 Blob，而不是让浏览器直接下载附件：后者要能开就得放开鉴权面。
   */
  inlineHtml: (
    id: string,
  ): Promise<{ filename: string; html: string; bytes: number; inlined: string[] }> =>
    apiRequest({ method: 'GET', path: `/api/v1/landing-pages/${id}/inline-html` }),
  /** 仅取最后保留的成功历史产物用于对照；不作为当前配置下载入口。 */
  historicalInlineHtml: (
    id: string,
  ): Promise<{ filename: string; html: string; bytes: number; inlined: string[] }> =>
    apiRequest({ method: 'GET', path: `/api/v1/landing-pages/${id}/historical-inline-html` }),

  list: (
    page: number,
    limit: number,
    filters: LandingPageListFilters = {},
  ): Promise<OffsetPagedResult<LandingPageDto>> =>
    apiRequestOffsetPaged<LandingPageDto>({
      method: 'GET',
      path: '/api/v1/landing-pages',
      query: { page, limit, ...filters },
    }),
  get: (id: string): Promise<LandingPageDto> =>
    apiRequest<LandingPageDto>({ method: 'GET', path: `/api/v1/landing-pages/${id}` }),
  create: (body: CreateLandingPageRequest): Promise<LandingPageDto> =>
    apiRequest<LandingPageDto>({ method: 'POST', path: '/api/v1/landing-pages', body }),
  update: (id: string, body: UpdateLandingPageRequest): Promise<LandingPageDto> =>
    apiRequest<LandingPageDto>({
      method: 'PATCH',
      path: `/api/v1/landing-pages/${id}`,
      body,
    }),
  regenerate: (id: string): Promise<LandingPageDto> =>
    apiRequest<LandingPageDto>({
      method: 'POST',
      path: `/api/v1/landing-pages/${id}/regenerate`,
    }),
  publish: (id: string): Promise<LandingPageDto> =>
    apiRequest<LandingPageDto>({
      method: 'POST',
      path: `/api/v1/landing-pages/${id}/publish`,
    }),
};
